import { memo, ReactElement, useCallback, useEffect, useRef } from 'react';
import { useScrollIntoView } from '@hooks/usescrollIntoView';
import { IGoogleSearchInput, IMapSearchInput } from '.';

const GoogleSearchInput = (props: IGoogleSearchInput): ReactElement => {
	const {
		id,
		type,
		name,
		value,
		label,
		error,
		required,
		disabled,
		setFieldValue,
		onBlur,
		maxLength,
		onKeyDown,
		className,
		placeholder,
		isAutoFocus,
		onChange,
		dataAttributes,
		labelClass,
		revalidate,
		revalidateError,
		handleVenueSelection,
	} = props;

	const inputRef = useRef<HTMLInputElement>(null);
	const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
	const { handleFocus } = useScrollIntoView(inputRef);

	useEffect(() => {
		if (inputRef.current && window.google?.maps?.places) {
			if (!autocompleteRef.current) {
				autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
					componentRestrictions: { country: 'IN' },
					fields: ['place_id', 'name', 'formatted_address', 'geometry'],
				});
			}
		}

		return (): void => {
			if (autocompleteRef.current) {
				window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
				autocompleteRef.current = null;
			}
		};
	}, []);

	const fetchPlaceDetails = useCallback(
		(inputText: string) => {
			if (!inputText || inputText.trim().length < 3) return;

			const service = new window.google.maps.places.PlacesService(document.createElement('div'));

			const reverseGeocode = (lat: number, lng: number): Promise<IMapSearchInput> => {
				return new Promise((resolve) => {
					const geocoder = new window.google.maps.Geocoder();
					const latlng = { lat, lng };

					geocoder.geocode({ location: latlng }, (results, status) => {
						if (status === 'OK' && results?.[0]) {
							const result = results[0];
							const addressComponents = {
								streetNumber: '',
								route: '',
								city: '',
								state: '',
								country: '',
								pincode: '',
								formatted_address: result.formatted_address,
							};
							result.address_components.forEach((component) => {
								if (component.types.includes('street_number')) addressComponents.streetNumber = component.long_name;
								if (component.types.includes('route')) addressComponents.route = component.long_name;
								if (component.types.includes('locality')) addressComponents.city = component.long_name;
								if (component.types.includes('administrative_area_level_1'))
									addressComponents.state = component.long_name;
								if (component.types.includes('country')) addressComponents.country = component.long_name;
								if (component.types.includes('postal_code')) addressComponents.pincode = component.long_name;
							});
							resolve(addressComponents);
						} else {
							resolve({});
						}
					});
				});
			};

			const request = {
				query: inputText,
				fields: ['name', 'geometry', 'formatted_address'],
			};

			service.findPlaceFromQuery(request, async (results, status) => {
				if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
					const place = results[0];
					if (place.geometry?.location) {
						const lat = place.geometry.location?.lat() ?? 0;
						const lng = place.geometry.location?.lng() ?? 0;
						const addressData = await reverseGeocode(lat, lng);

						const venueData: IMapSearchInput = {
							lat: lat,
							lng: lng,
							name: place.name ?? '',
							routeName: '',
							subLocality: '',
							formattedAddress: addressData.formatted_address ?? '',
							pincode: addressData.pincode ?? '',
							city: addressData.city ?? '',
							state: addressData.state ?? '',
						};
						handleVenueSelection(venueData);
					}
				}
			});
		},
		[handleVenueSelection]
	);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>): void => {
			if (name && setFieldValue) {
				setFieldValue(name, e.target.value.replace(/^\s+/g, ''));
			}
			if (onChange && name) {
				onChange(e);
			}
		},
		[name, onChange, setFieldValue]
	);

	const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
		onBlur?.(e);
		fetchPlaceDetails(e.target.value);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): boolean | undefined => {
		if (e.key === 'Enter') {
			e.preventDefault();
			e.stopPropagation();
			fetchPlaceDetails(e.currentTarget.value);
			return false;
		}
		onKeyDown?.(e);
	};

	useEffect(() => {
		if (isAutoFocus && inputRef.current) {
			inputRef.current.focus();
		}
	}, [isAutoFocus]);

	const dataProps: { [key: string]: string } = {};
	if (dataAttributes) {
		Object.keys(dataAttributes).forEach((key) => {
			dataProps[`data-${key}`] = dataAttributes[key];
		});
	}

	return (
		<>
			<label htmlFor={id} className={`${required ? 'required' : ''} ${labelClass ?? ''}`}>
				{label}
			</label>
			<input
				id={id ?? 'map-input'}
				name={name}
				type={type}
				value={value}
				ref={inputRef}
				onBlur={handleInputBlur}
				onFocus={handleFocus}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				disabled={disabled}
				autoComplete="off"
				maxLength={maxLength}
				className={className}
				placeholder={placeholder}
				{...dataProps}
			/>

			{error && <p className={`error-message ${error && 'show'}`}>{error}</p>}
			{revalidate && <p className={`error-message ${revalidateError && 'show'}`}>{revalidateError}</p>}
		</>
	);
};

export default memo(GoogleSearchInput);
